/**
 * Reglas puras de columnas para las hojas Excel de Liquidaciones Agencia.
 *
 * Están en un archivo aparte, sin dependencias de Angular ni de exceljs, para que
 * puedan probarse con tests unitarios rápidos (sin TestBed y sin cargar el
 * componente completo, que arrastra librerías pesadas).
 */

/**
 * Columnas que son IDENTIFICADORES, no montos: NUNCA deben totalizarse en la fila
 * de TOTALES. Sumar un identificador produce un número sin significado que el
 * cliente lee como si fuera un monto.
 *
 * Caso real (liq 1612, reporte cliente 2026-07-27): la hoja Transportes mostraba
 * 10.611 en la columna "Transport Order ID" — era 3.537 sumado una vez por cada
 * uno de los 3 contenedores.
 *
 * `nroFacturaVenta` y `nroNotaCobro` hoy llegan como string (el filtro por
 * `typeof === 'number'` no los toma), pero se incluyen por si el backend cambia
 * su tipo en el futuro.
 */
export const COLUMNAS_NO_TOTALIZABLES: ReadonlySet<string> = new Set([
    // Hoja Transportes
    'ot',
    'transportOrderId',
    // Hojas Agenciamiento / Contenedores
    'nroIE',
    'nroLiquidacion',
    'liquidacion',
    'nroViaje',
    'nroDespacho',
    'dus',
    'numeroBl',
    'reserva',
    'sello',
    'nroFacturaVenta',
    'nroNotaCobro',
]);

/**
 * Indica si una columna puede totalizarse en la fila de TOTALES.
 */
export function esColumnaTotalizable(key: string): boolean {
    return !COLUMNAS_NO_TOTALIZABLES.has(key);
}

/**
 * Decide si corresponde aplicar el filtro de columnas dinámicas de servicios.
 *
 * Regla de negocio 11 (MEJ-08): las columnas cuyos valores están todos en 0 NO se
 * renderizan.
 *
 * ⚠️ Un array VACÍO es información válida — significa "ningún servicio tiene
 * monto" — y por lo tanto SÍ debe filtrar (ocultando todas las columnas de
 * servicios). El filtro solo se omite cuando el backend no envió el campo
 * (`undefined` / `null`), por compatibilidad con respuestas antiguas.
 *
 * El bug corregido el 2026-07-27 usaba `serviciosPresentes.length > 0`, que
 * confundía "no hay servicios" con "no tengo información" y mostraba la sábana
 * completa de columnas en 0.
 */
export function debeFiltrarColumnasDeServicios(
    serviciosPresentes: string[] | null | undefined
): boolean {
    return Array.isArray(serviciosPresentes);
}

/**
 * Devuelve la UNIÓN ordenada de las claves de TODAS las filas, en vez de solo las
 * de la primera (`Object.keys(firstRow)`).
 *
 * ERR-04 (OPB 2026-09-30 11:34): la exportación combinada de varias liquidaciones
 * (`combinedData` en el componente) arma cada hoja aplanando las filas de todas
 * las liquidaciones seleccionadas con `flatMap`. Si la PRIMERA fila del arreglo
 * combinado pertenece a una liquidación que no generó una columna de servicio
 * dinámico (p.ej. "Porteos"), esa columna desaparecía del Excel — aunque su monto
 * sí sumara al Total Venta y otra liquidación del mismo consolidado sí la tuviera.
 * Caso real: cliente EUROFISH, liqs 1769/1775/1780 (solo transporte, sin packing
 * lists) — "Porteos" y "Falso Flete Depósito" no aparecían en el Excel combinado.
 *
 * El orden se conserva: primero las claves de la primera fila, en su propio
 * orden; luego las claves nuevas de las filas siguientes, en orden de PRIMERA
 * aparición. Así las columnas fijas quedan en la misma posición que tendrían en
 * la exportación de una sola liquidación, y las columnas dinámicas nuevas se
 * agregan al final sin reordenar las existentes.
 *
 * Esta función NO decide qué columnas se ocultan por no tener monto — eso sigue
 * siendo trabajo de `debeFiltrarColumnasDeServicios` /
 * `filtrarColumnasConServiciosPresentes`, que se aplican DESPUÉS sobre el
 * resultado de esta unión.
 */
export function unionKeysPreservingOrder(
    rows: ReadonlyArray<Record<string, unknown>>
): string[] {
    const vistas = new Set<string>();
    const claves: string[] = [];
    for (const fila of rows) {
        for (const clave of Object.keys(fila)) {
            if (!vistas.has(clave)) {
                vistas.add(clave);
                claves.push(clave);
            }
        }
    }
    return claves;
}

/**
 * Columnas de TOTALES que, por regla de OPB ("Punto C", PR #308, 2026-08, y
 * confirmada de nuevo por email 2026-10-09 19:36 de Martín Hidalgo), deben
 * quedar SIEMPRE como las últimas columnas de cada hoja, en este orden relativo
 * entre sí. Cubre los distintos nombres de clave que usan las distintas hojas
 * (Agenciamiento/Transportes/Servicios Extra vs. Totales/Pallets).
 *
 * El orden de este array ES el orden final entre ellas cuando varias están
 * presentes en la misma hoja (p.ej. Transportes: Gestión OPB, Total Venta,
 * Total Traspaso).
 */
export const TOTALES_SIEMPRE_AL_FINAL: readonly string[] = [
    'gestionOpb',
    'totalVenta',
    'totalTraspaso',
    'gestionOpbNeto',
    'totalVentaNeto',
    'totalTraspasoBruto',
    'totalVentaTransporte',
    'totalTraspasoTransporte',
    'totalGeneral',
];

const TOTALES_SIEMPRE_AL_FINAL_SET: ReadonlySet<string> = new Set(
    TOTALES_SIEMPRE_AL_FINAL
);

/**
 * Regresión ERR-04→Punto-C (OPB email 2026-10-09 19:36): `unionKeysPreservingOrder`
 * solo garantiza orden de PRIMERA aparición, no que las columnas de totales
 * queden siempre últimas. Cuando una liquidación posterior del arreglo
 * combinado introduce una columna de servicio dinámica que la PRIMERA
 * liquidación no tenía, esa columna nueva se agrega al final de la unión — es
 * decir, DESPUÉS de "Gestión OPB"/"Total Venta"/"Total Traspaso" que la
 * primera liquidación ya había fijado antes. El resultado viola la regla de
 * OPB de que esas columnas de totales son siempre las últimas de la hoja.
 *
 * Esta función es el paso final, aplicado DESPUÉS de `unionKeysPreservingOrder`
 * (y después de cualquier filtro de columnas dinámicas de servicios): saca las
 * claves de `TOTALES_SIEMPRE_AL_FINAL` de donde hayan quedado y las vuelve a
 * poner al final, en el orden relativo de `TOTALES_SIEMPRE_AL_FINAL`. Las
 * claves que no están en la hoja simplemente no aparecen — no se inventan
 * columnas vacías.
 */
export function conTotalesAlFinal(claves: ReadonlyArray<string>): string[] {
    const presentes = new Set(claves);
    const resto = claves.filter(
        (clave) => !TOTALES_SIEMPRE_AL_FINAL_SET.has(clave)
    );
    const totalesEnOrden = TOTALES_SIEMPRE_AL_FINAL.filter((clave) =>
        presentes.has(clave)
    );
    return [...resto, ...totalesEnOrden];
}

/**
 * Convierte una clave camelCase en un rótulo legible para el Excel.
 *
 * Es el fallback para las columnas que NO están en el mapa de rótulos, que desde el
 * pase a columnas dinámicas son justamente las de los servicios nuevos. Antes el
 * fallback sólo capitalizaba la primera letra, así que el cliente veía
 * `ServicioGateOutV2` en vez de "Servicio Gate Out V2" — detectado en el Excel real
 * de la liq 1606 el 2026-07-28.
 *
 * No toca las columnas que sí están en el mapa: ahí el rótulo lo decide el mapa.
 */
export function humanizarClave(clave: string): string {
    if (!clave) return '';
    const conEspacios = clave
        // separa antes de cada mayúscula que sigue a una minúscula o dígito
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        // separa antes de un grupo de dígitos que sigue a una letra
        .replace(/([A-Za-z])(\d)/g, '$1 $2');
    return conEspacios.charAt(0).toUpperCase() + conEspacios.slice(1);
}
